type RequestConfig = {
  headers?: Record<string, string>;
  params?: Record<string, string>;
};

class HttpClient {
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL.endsWith("/") ? baseURL : `${baseURL}/`;
  }

  private createUrl(endpoint: string, params?: Record<string, string>): string {
    const cleanEndpoint = endpoint.startsWith("/")
      ? endpoint.slice(1)
      : endpoint;
    const fullUrl = this.baseURL + cleanEndpoint;
    const url = new URL(fullUrl);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, value);
      });
    }
    return url.toString();
  }

  private async request<T>(
    method: string,
    endpoint: string,
    config?: RequestConfig,
    body?: unknown,
  ): Promise<T> {
    const url = this.createUrl(endpoint, config?.params);
    const headers = { ...config?.headers };

    if (!(body instanceof FormData)) {
      headers["Content-Type"] = "application/json";
    }

    const response = await fetch(url, {
      method,
      headers,
      body: body instanceof FormData ? body : JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async get<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    return this.request<T>("GET", endpoint, config);
  }

  async post<T>(
    endpoint: string,
    data?: unknown,
    config?: RequestConfig,
  ): Promise<T> {
    return this.request<T>("POST", endpoint, config, data);
  }

  async put<T>(
    endpoint: string,
    data?: unknown,
    config?: RequestConfig,
  ): Promise<T> {
    return this.request<T>("PUT", endpoint, config, data);
  }

  async delete<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    return this.request<T>("DELETE", endpoint, config);
  }
}

export const httpClient = new HttpClient(import.meta.env.VITE_API_BASE_URL);
